import { useEffect, useRef, useState } from "react";
import { RotatingLines } from "react-loader-spinner";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import MainContainer from "../../../components/MainContainer";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { concatenateDate, xReqId } from "../../../utils";

const CancelEventsPage = () => {
	const statusesMap = new Map([
		["ACCEPTED", { css: "border-success", gr: "Αποδεκτό" }],
		["PENDING", { css: "border-warning", gr: "Εκκρεμεί" }],
	]);
	const allowedStatuses = ["PENDING", "ACCEPTED"];
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [declarations, setDeclarations] = useState([]);
	const [cancelSubmitLoading, setCancelSubmitLoading] = useState(false);
	const [errorDesc, setErrorDesc] = useState("Κάτι έχει πάει στραβά. Παρακαλώ δοκιμάστε αργότερα.");
	const [selectedMinor, setSelectedMinor] = useState(undefined);
	const [showInputField, setShowInputField] = useState(false);
	const [showSubmitButton, setShowSubmitButton] = useState(false);
	const [companionAfm, setCompanionAfm] = useState("");
	const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
	const [isIButtonDisabled, setIsIButtonDisabled] = useState(false);
	const [isOtherCompanionButtonDisabled, setIsOtherCompanionButtonDisabled] = useState(false);
	const inputRef = useRef(null);

	const navigate = useNavigate();

	useDocumentTitle("Άδειες συνοδείας ανηλίκων");

	const handleRadioChange = (event) => {
		const selectedMinorData = declarations.find((item) => item.declarationId === event.target.value);
		setSelectedMinor(selectedMinorData);
	};

	const cancelEventTicket = async () => {
		const userConfirmed = window.confirm("Είστε σίγουροι ότι θέλετε να ακυρώσετε το αίτημα συνοδείας;");

		if (!userConfirmed) {
			return;
		}

		setCancelSubmitLoading(true);
		const url = `/api/tickets/dependents/declarations/${selectedMinor?.declarationId}/cancel`;
		try {
			// Send a PUT request to the API endpoint with the ticket data.
			const res = await fetch(url, {
				method: "DELETE",
				credentials: "include", // Include cookies for authentication.
				headers: {
					"Content-Type": "application/json",
					"x-request-id": xReqId,
				}, // Specify the request body content type.
				body: JSON.stringify([]), // Serialize the ticket data to JSON.
			});

			// If the response is successful, navigate to the success page, otherwise navigate to the failure page.
			if (res.ok) {
				setCancelSubmitLoading(false);
				const responseData = await res.json();
				navigate("/tickets/success-cancel", {
					state: { payload: responseData, type: "cancel" },
				});
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			const genericErrorMessage =
				"Προέκυψε σφάλμα συστήματος. Παρακαλώ δοκιμάστε αργότερα και εάν το πρόβλημα εξακολουθεί επικοινωνήστε με την ομάδα υποστήριξης.";
			setCancelSubmitLoading(false);
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.exceptions?.errors?.[0]?.errorDescription;
				toast.error(appError ?? genericErrorMessage);
				setLoading(false);
				setCancelSubmitLoading(false);
      } catch {
				toast.error(genericErrorMessage);
				setLoading(false);
				setCancelSubmitLoading(false);
			}
		}
	};

	const handleInputChange = (e) => {
		const value = e.target.value;
		if (/^\d*$/.test(value)) {
			setCompanionAfm(value);
			setIsSubmitButtonDisabled(value.length !== 9);
		}
	};

	const handleSubmit = async () => {
		setLoading(true);
		const url = "/api/tickets/dependents/declarations/modify";

		const newCompanionData = {
			declarationId: selectedMinor?.declarationId,
			companionAfm: isIButtonDisabled ? "" : companionAfm,
		};

		try {
			const res = await fetch(url, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					"x-request-id": xReqId,
				},
				body: JSON.stringify(newCompanionData),
			});

			if (res.ok) {
				setSelectedMinor(null);
				setDeclarations([]);
				setShowInputField(false);
				setCompanionAfm("");

				fetch("/api/tickets/dependents/declarations", {
					method: "GET",
					credentials: "include",
					headers: {
						"Content-Type": "application/json",
						"x-request-id": xReqId,
					},
				})
					.then((response) => {
						if (response.ok) {
							return response.json();
						} else {
							throw response;
						}
					})
					.then((data) => {
						const sortedData = data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
						setDeclarations(sortedData);
						setSelectedMinor(undefined);
						setShowSubmitButton(false);
						setIsIButtonDisabled(false);
						setIsOtherCompanionButtonDisabled(false);
						setLoading(false);
					})
					.catch(async (error) => {
						const errorResponse = await error.text();
						setError(true);
						setLoading(false);
						try {
							const json = JSON.parse(errorResponse);
							const appError = json?.exceptions?.errors?.[0]?.errorDescription;
							setErrorDesc(appError);
            } catch {}
					});

				setLoading(false);
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			const genericErrorMessage =
				"Προέκυψε σφάλμα συστήματος. Παρακαλώ δοκιμάστε αργότερα και εάν το πρόβλημα εξακολουθεί επικοινωνήστε με την ομάδα υποστήριξης.";
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.exceptions?.errors?.[0]?.errorDescription;
				toast.error(appError ?? genericErrorMessage);
				setLoading(false);
      } catch {
				toast.error(genericErrorMessage);
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		if (showInputField && inputRef.current) {
			inputRef.current.focus();
		}
	}, [showInputField]);

	useEffect(() => {
		setLoading(true);

		fetch("/api/tickets/dependents/declarations", {
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"x-request-id": xReqId,
			},
		})
			.then((response) => {
				if (response.ok) {
					return response.json();
				} else {
					throw response;
				}
			})
			.then((data) => {
				const sortedData = data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
				setDeclarations(sortedData);
				setLoading(false);
			})
			.catch(async (error) => {
				const errorResponse = await error.text();
				setError(true);
				setLoading(false);
				try {
					const json = JSON.parse(errorResponse);
					const appError = json?.exceptions?.errors?.[0]?.errorDescription;
					setErrorDesc(appError);
        } catch {}
			});
	}, []);

	if (loading) {
		return (
			<MainContainer>
				<div className="col-lg-12 d-flex flex-fill justify-content-center align-items-center">
					<output aria-live="polite" aria-label="Γίνεται φόρτωση των αδειών συνοδείας" className="text-center">
						<span className="visually-hidden">Γίνεται φόρτωση των αδειών συνοδείας.</span>
						<RotatingLines
							visible={true}
							height="96"
							width="96"
							color="grey"
							strokeWidth="5"
							strokeColor="#003476"
							animationDuration="0.75"
							ariaLabel="Γίνεται φόρτωση των αδειών συνοδείας"
						/>
					</output>
				</div>
			</MainContainer>
		);
	}

	const handleIButtonClick = () => {
		setShowInputField(false);
		setShowSubmitButton(true);
		setIsIButtonDisabled(true);
		setIsOtherCompanionButtonDisabled(false);
		setIsSubmitButtonDisabled(false);
	};

	const handleOtherCompanionButtonClick = () => {
		setShowInputField(true);
		setShowSubmitButton(true);
		setIsIButtonDisabled(false);
		setIsOtherCompanionButtonDisabled(true);
		setIsSubmitButtonDisabled(true);
	};

	const getBody = () => {
		if (error) {
			return (
				<div className="p-4 border bg-body-tertiary border-danger rounded mb-4 col-7">
					<p className="mb-0">{errorDesc}</p>
				</div>
			);
		}

		if (declarations.length === 0) {
			return (
				<div className="p-4 border bg-body-tertiary border-primary rounded mb-4 col-7">
					<p className="mb-0">Δεν βρέθηκε καμία ενεργή αδειοδότηση</p>
				</div>
			);
		}
		return (
			<>
				{/* top container */}
				<div className="container">
					<h1 className="mb-4">Άδειες συνοδείας ανηλίκων</h1>
					<fieldset className="border-0 p-0 m-0">
						<legend className="fs-4 mb-3">Επιλέξτε αδειοδότηση</legend>
						{declarations
							?.filter((declaration) => allowedStatuses.includes(declaration.status))
							.map((item) => (
								<div key={item?.declarationId} className="choose-minor-option px-2 py-4 border bg-white rounded mb-3">
									<label
										className="choose-minor-option-label form-check-label d-flex align-items-start w-100 m-0"
										htmlFor={`flexRadioDefault${item?.declarationId}`}
									>
										<input
											className="form-check-input fs-5 mx-2 mt-1"
											type="radio"
											name="selectedDeclaration"
											id={`flexRadioDefault${item?.declarationId}`}
											value={item?.declarationId}
											checked={item?.declarationId === selectedMinor?.declarationId}
											onChange={handleRadioChange}
										/>
										<span className="flex-grow-1 m-lg-0 m-2">
											<span className="d-block fw-bold fs-4 mb-2">
												{item?.dependentName} (γεν. {item?.birthDate}), {item?.eventDescription} (
												{concatenateDate(item?.eventDateTime)})
											</span>
											<span className="d-flex justify-content-between align-items-center">
												<span className="mb-0 fs-6 text-secondary">
													Συνοδός: {item?.companionName} (AΦΜ: {item?.companionAfm}), Κωδικός εισιτηρίου:{" "}
													{item?.ticketCode}
												</span>
												<span
													className={`badge rounded-pill border border-2 ms-2 ${statusesMap.get(item?.status)?.css ?? "border-warning"} bg-transparent text-dark`}
													style={{ borderWidth: "2px" }}
												>
													{statusesMap.get(item?.status)?.gr ?? item.status}
												</span>
											</span>
										</span>
									</label>
								</div>
							))}
					</fieldset>
				</div>

				{/* botttom container */}
				{selectedMinor && (
					<div className="container">
						<div className="row align-items-center">
							<div className="col-auto">
								{(cancelSubmitLoading || loading) && (
									<div className="d-flex justify-content-center align-items-center" style={{ width: "200px" }}>
										<RotatingLines
											visible={true}
											color="grey"
											strokeWidth="5"
											strokeColor="white"
											animationDuration="0.75"
											ariaLabel="rotating-lines-loading"
											wrapperStyle={{}}
											wrapperClass=""
										/>
									</div>
								)}
								{selectedMinor.status === "PENDING" ? (
									<button
										type="button"
										onClick={cancelEventTicket}
										className="btn btn-danger btn-lg"
										disabled={cancelSubmitLoading}
										style={{
											width: "200px",
											padding: "10px",
											fontSize: "1.25rem",
											fontWeight: 400,
										}}
									>
										{cancelSubmitLoading && selectedMinor.status === "PENDING" ? (
											<div className="d-flex justify-content-center align-items-center" style={{ width: "200px" }}>
												<div>Ακύρωση</div>
											</div>
										) : (
											"Ακύρωση"
										)}
									</button>
								) : (
									selectedMinor.status === "ACCEPTED" && (
										<div className="col">
											<div className="d-flex p-3 flex-column justify-content-center">
												<h5 className="mb-3">Αλλαγή συνοδείας</h5>
												<div className="d-flex gap-3 align-items-start">
													<button
														type="button"
														onClick={handleIButtonClick}
														className="btn btn-primary btn-lg"
														disabled={isIButtonDisabled}
														style={{
															width: "200px",
															padding: "10px 20px",
															fontSize: "1.25rem",
															fontWeight: 400,
														}}
													>
														Εγώ
													</button>
													<button
														type="button"
														onClick={handleOtherCompanionButtonClick}
														className="btn btn-primary btn-lg"
														disabled={isOtherCompanionButtonDisabled}
														style={{
															width: "200px",
															padding: "10px 20px",
															fontSize: "1.25rem",
															fontWeight: 400,
														}}
													>
														Άλλος συνοδός
													</button>
													<form className="d-flex align-items-center" onSubmit={(e) => e.preventDefault()}>
														{showInputField && (
															<>
																<label htmlFor="cancel-events-companion-afm" className="visually-hidden">
																	ΑΦΜ συνοδού
																</label>
																<input
																	type="text"
																	id="cancel-events-companion-afm"
																	className="form-control"
																	placeholder="ΑΦΜ συνοδού"
																	value={companionAfm}
																	onChange={handleInputChange}
																	maxLength="9"
																	minLength="9"
																	ref={inputRef}
																	style={{
																		width: "200px",
																		padding: "10px 20px",
																		fontSize: "1.25rem",
																		fontWeight: 400,
																	}}
																/>
															</>
														)}

														{showSubmitButton && (
															<button
																className="btn btn-secondary btn-lg ms-2"
																disabled={isSubmitButtonDisabled}
																onClick={handleSubmit}
																type="submit"
																style={{
																	width: "200px",
																	padding: "10px 20px",
																	fontSize: "1.25rem",
																	fontWeight: 400,
																}}
															>
																Αποστολή
															</button>
														)}
													</form>
												</div>
											</div>
										</div>
									)
								)}
							</div>
						</div>
					</div>
				)}
			</>
		);
	};

	return (
		<MainContainer>
			<div className="col-lg-12">
				<Link to="/tickets/choose-action" className="btn btn-back mb-4">
					<svg className="govgr-svg-icon--caret govgr-svg-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
						<path d="M18,22V2L6,12L18,22z"></path>
					</svg>
					Πίσω
				</Link>
				{getBody()}
			</div>
		</MainContainer>
	);
};
export default CancelEventsPage;
